// Direct Manager Discrepancy Audit — data-fetching hooks (ADR-010 Phase 6).
//
// Three per-page hooks. Page-local state, no global store.
// Infinite-loading shape (mirrors the bench report's pattern):
//   - items accumulates across loadMore() calls
//   - When filters change, the list resets and a fresh fetch happens
//   - loadMore() fetches the next page using endCursor and appends
//   - refetch() forces a fresh fetch from the top (e.g. after "Trigger now")

import { useCallback, useEffect, useRef, useState } from 'react';
import { directManagerDiscrepanciesApi } from '../services/api/directManagerDiscrepanciesApi';

const emptyPageInfo = { hasNextPage: false, endCursor: null };

/**
 * Runs list (Page 1).
 * Filters: { sourceSystem, status, limit }.
 */
export const useRunsList = (filters = {}) => {
  const [items, setItems] = useState([]);
  const [pageInfo, setPageInfo] = useState(emptyPageInfo);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const filterKey = JSON.stringify(filters);
  // Always carry the latest pageInfo into loadMore without re-binding it
  // (avoids re-creating the loadMore reference on every page fetch).
  const pageInfoRef = useRef(emptyPageInfo);
  pageInfoRef.current = pageInfo;

  const fetchRuns = useCallback(async (cursor, append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      const result = await directManagerDiscrepanciesApi.listRuns({ ...filters, cursor });
      const nextItems = result?.items || [];
      const nextPageInfo = result?.pageInfo || emptyPageInfo;
      setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
      setPageInfo(nextPageInfo);
    } catch (err) {
      setError(err);
      if (!append) {
        setItems([]);
        setPageInfo(emptyPageInfo);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Filter change → fresh fetch from top.
  useEffect(() => {
    fetchRuns(undefined, false);
  }, [fetchRuns]);

  const loadMore = useCallback(() => {
    const pi = pageInfoRef.current;
    if (pi?.hasNextPage && pi.endCursor) {
      fetchRuns(pi.endCursor, true);
    }
  }, [fetchRuns]);

  const refetch = useCallback(() => fetchRuns(undefined, false), [fetchRuns]);

  return { items, pageInfo, isLoading, isLoadingMore, error, loadMore, refetch };
};

/**
 * Run detail (Page 2 — top region).
 */
export const useRunDetail = (runId) => {
  const [run, setRun] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRun = useCallback(async () => {
    if (!runId) {
      setRun(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await directManagerDiscrepanciesApi.getRun(runId);
      setRun(result);
    } catch (err) {
      setError(err);
      setRun(null);
    } finally {
      setIsLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    fetchRun();
  }, [fetchRun]);

  return { run, isLoading, error, refetch: fetchRun };
};

/**
 * Run discrepancies (Page 2 — table region).
 * Filters: { runId, match, mismatchReason, mappingStatus, employeeId, limit }.
 */
export const useRunDiscrepancies = (filters = {}) => {
  const [items, setItems] = useState([]);
  const [pageInfo, setPageInfo] = useState(emptyPageInfo);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const filterKey = JSON.stringify(filters);
  const pageInfoRef = useRef(emptyPageInfo);
  pageInfoRef.current = pageInfo;

  const fetchDiscrepancies = useCallback(async (cursor, append = false) => {
    if (!filters.runId) {
      setItems([]);
      setPageInfo(emptyPageInfo);
      setIsLoading(false);
      return;
    }
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      const result = await directManagerDiscrepanciesApi.listDiscrepancies({ ...filters, cursor });
      const nextItems = result?.items || [];
      const nextPageInfo = result?.pageInfo || emptyPageInfo;
      setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
      setPageInfo(nextPageInfo);
    } catch (err) {
      setError(err);
      if (!append) {
        setItems([]);
        setPageInfo(emptyPageInfo);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    fetchDiscrepancies(undefined, false);
  }, [fetchDiscrepancies]);

  const loadMore = useCallback(() => {
    const pi = pageInfoRef.current;
    if (pi?.hasNextPage && pi.endCursor) {
      fetchDiscrepancies(pi.endCursor, true);
    }
  }, [fetchDiscrepancies]);

  const refetch = useCallback(() => fetchDiscrepancies(undefined, false), [fetchDiscrepancies]);

  return { items, pageInfo, isLoading, isLoadingMore, error, loadMore, refetch };
};
